'use client';

import React from 'react';
import { type TargetAndTransition, useReducedMotion } from 'motion/react';

import { MOTION_TAGS, type MotionTag } from './tags';
import { useDesignMotion } from './useDesignMotion';

export type LoopPreset = 'float' | 'spin' | 'twinkle' | 'breathe' | 'drift' | 'pulse';

interface LoopProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  'onAnimationStart' | 'onDrag' | 'onDragStart' | 'onDragEnd'
> {
  preset: LoopPreset;
  as?: MotionTag;
  /** Seconds per cycle (each preset has a sensible default) */
  duration?: number;
  /** Start part-way through the cycle, so neighbours don't move in sync (seconds) */
  offset?: number;
}

/**
 * Endless, slow decorative motion: illustrations bobbing, stars twinkling, glows breathing,
 * rims turning. Stops entirely for people who prefer reduced motion.
 */
export function Loop({ preset, as = 'div', duration, offset = 0, children, ...rest }: LoopProps) {
  const motion = useDesignMotion();
  const reduce = useReducedMotion();
  const Component = MOTION_TAGS[as];

  const presets: Record<
    LoopPreset,
    { animate: TargetAndTransition; seconds: number; linear?: boolean }
  > = {
    float: { animate: { y: [0, motion.float.y, 0] }, seconds: motion.float.duration },
    spin: { animate: { rotate: [0, 360] }, seconds: 18, linear: true },
    twinkle: { animate: { opacity: [1, 0.25, 1], scale: [1, 0.7, 1] }, seconds: 3 },
    breathe: { animate: { opacity: [1, 0.55, 1], scale: [1, 1.06, 1] }, seconds: 7 },
    drift: {
      animate: { x: ['0%', '-4%', '0%'], y: ['0%', '5%', '0%'], scale: [1, 1.08, 1] },
      seconds: 16,
    },
    pulse: { animate: { opacity: [1, 0.4, 1] }, seconds: 2.4 },
  };
  const { animate, seconds, linear } = presets[preset];
  const cycle = duration ?? seconds;

  if (reduce) return <Component {...rest}>{children}</Component>;

  return (
    <Component
      animate={animate}
      transition={{
        duration: cycle,
        repeat: Infinity,
        ease: linear ? 'linear' : 'easeInOut',
        delay: -offset,
      }}
      {...rest}
    >
      {children}
    </Component>
  );
}

/** An illustration bobbing gently. */
export function Float(props: Omit<LoopProps, 'preset'>) {
  return <Loop preset='float' {...props} />;
}
